<div class="import-container">
  <h1 class="mat-h1">Import Leavers</h1>

  <!-- Step 1: File Picker -->
  <mat-card *ngIf="step === 1">
    <mat-card-title>Step 1: Select File</mat-card-title>
    <mat-card-content>
      <p>Select an XLSX spreadsheet containing leaver records.</p>
      <button mat-raised-button color="primary" (click)="fileInput.click()">
        <mat-icon>cloud_upload</mat-icon> Choose File
      </button>
      <input #fileInput type="file" accept=".xlsx,.xls" (change)="onFileSelected($event)" style="display:none">
      <span *ngIf="fileName" class="file-name">{{ fileName }}</span>
    </mat-card-content>
  </mat-card>

  <!-- Step 2: Sheet Selector -->
  <mat-card *ngIf="step === 2">
    <mat-card-title>Step 2: Select Sheet</mat-card-title>
    <mat-card-content>
      <p>File: <strong>{{ fileName }}</strong></p>
      <mat-form-field>
        <mat-select placeholder="Select a sheet" [(value)]="selectedSheet">
          <mat-option *ngFor="let name of sheetNames" [value]="name">{{ name }}</mat-option>
        </mat-select>
      </mat-form-field>
      <div class="button-row">
        <button mat-raised-button (click)="resetImport()">Back</button>
        <button mat-raised-button color="primary" [disabled]="!selectedSheet" (click)="onSheetSelected()">
          Parse Sheet
        </button>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Step 3: Preview Table -->
  <div *ngIf="step === 3">
    <mat-card class="stats-card">
      <mat-card-content>
        <div class="stats-row">
          <span><strong>{{ totalRows }}</strong> rows parsed</span>
          <span class="stat-item">
            <mat-icon style="color:#4caf50;vertical-align:middle">check_circle</mat-icon>
            <strong>{{ newRecords }}</strong> new
          </span>
          <span class="stat-item">
            <mat-icon style="color:#ff9800;vertical-align:middle">warning</mat-icon>
            <strong>{{ weakDuplicates }}</strong> possible duplicates
          </span>
          <span class="stat-item">
            <mat-icon style="color:#f44336;vertical-align:middle">error</mat-icon>
            <strong>{{ strongDuplicates }}</strong> exact duplicates
          </span>
          <span><strong>{{ selection.selected.length }}</strong> selected for import</span>
        </div>
        <mat-progress-spinner *ngIf="checkingDuplicates" mode="indeterminate" diameter="24" class="inline-spinner"></mat-progress-spinner>
        <span *ngIf="checkingDuplicates"> Checking for duplicates...</span>
      </mat-card-content>
    </mat-card>

    <mat-card>
      <mat-card-content>
        <table mat-table [dataSource]="dataSource" class="import-table">

          <!-- Checkbox Column -->
          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef>
              <mat-checkbox (change)="masterToggle()"
                            [checked]="selection.hasValue() && isAllSelected()"
                            [indeterminate]="selection.hasValue() && !isAllSelected()">
              </mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let row">
              <mat-checkbox (click)="$event.stopPropagation()"
                            (change)="selection.toggle(row)"
                            [checked]="selection.isSelected(row)">
              </mat-checkbox>
            </td>
          </ng-container>

          <!-- Duplicate Status Column -->
          <ng-container matColumnDef="duplicateStatus">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let row">
              <mat-icon [style.color]="getDuplicateColor(row)"
                        [matTooltip]="getDuplicateTooltip(row)">
                {{ getDuplicateIcon(row) }}
              </mat-icon>
            </td>
          </ng-container>

          <!-- Student ID Column -->
          <ng-container matColumnDef="student_id">
            <th mat-header-cell *matHeaderCellDef>Student ID</th>
            <td mat-cell *matCellDef="let row">{{ row.student_id }}</td>
          </ng-container>

          <!-- First Name Column -->
          <ng-container matColumnDef="firstname">
            <th mat-header-cell *matHeaderCellDef>First Name</th>
            <td mat-cell *matCellDef="let row">{{ row.firstname }}</td>
          </ng-container>

          <!-- Middle Names Column -->
          <ng-container matColumnDef="middlenames">
            <th mat-header-cell *matHeaderCellDef>Middle Names</th>
            <td mat-cell *matCellDef="let row">{{ row.middlenames }}</td>
          </ng-container>

          <!-- Last Name Column -->
          <ng-container matColumnDef="lastname">
            <th mat-header-cell *matHeaderCellDef>Last Name</th>
            <td mat-cell *matCellDef="let row">{{ row.lastname }}</td>
          </ng-container>

          <!-- Birthdate Column -->
          <ng-container matColumnDef="birthdate">
            <th mat-header-cell *matHeaderCellDef>Birthdate</th>
            <td mat-cell *matCellDef="let row">{{ formatDate(row.birthdate) }}</td>
          </ng-container>

          <!-- Email Column -->
          <ng-container matColumnDef="emailaddress">
            <th mat-header-cell *matHeaderCellDef>Email</th>
            <td mat-cell *matCellDef="let row">{{ row.emailaddress }}</td>
          </ng-container>

          <!-- Last Year Column -->
          <ng-container matColumnDef="lastyear">
            <th mat-header-cell *matHeaderCellDef>Last Year</th>
            <td mat-cell *matCellDef="let row">{{ row.lastyear }}</td>
          </ng-container>

          <!-- House Column -->
          <ng-container matColumnDef="housename">
            <th mat-header-cell *matHeaderCellDef>House</th>
            <td mat-cell *matCellDef="let row">{{ row.housename }}</td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"
              [class.strong-duplicate]="row._duplicateStatus === 'strong'"
              [class.weak-duplicate]="row._duplicateStatus === 'weak'">
          </tr>
        </table>

        <mat-paginator [pageSizeOptions]="[25, 50, 100]" showFirstLastButtons></mat-paginator>
      </mat-card-content>
    </mat-card>

    <div class="button-row">
      <button mat-raised-button (click)="resetImport()">Start Over</button>
      <button mat-raised-button color="primary"
              [disabled]="importing || selection.selected.length === 0"
              (click)="doImport()">
        <mat-icon>cloud_upload</mat-icon>
        Import {{ selection.selected.length }} Records
      </button>
      <mat-progress-spinner *ngIf="importing" mode="indeterminate" diameter="24" class="inline-spinner"></mat-progress-spinner>
    </div>
  </div>

  <!-- Step 4: Import Result -->
  <mat-card *ngIf="step === 4">
    <mat-card-title>Import Complete</mat-card-title>
    <mat-card-content>
      <p>{{ importResult }}</p>
      <button mat-raised-button color="primary" (click)="resetImport()">Import Another File</button>
    </mat-card-content>
  </mat-card>
</div>
